export interface CampaignCriteriaOption {
  key: string;
  value: string;
  id?: number;
}

export interface CampaignCriteria {
  id: number;
  label: string;
  weight: 'strong' | 'medium' | 'low' | 'weak';
  type: 'singleSelect' | 'multiSelect' | string;
  options: CampaignCriteriaOption[];
}

export type CriteriaAnswers = Record<string, string | string[]>;

export const getStrongCriteria = (criteriaList: CampaignCriteria[]) => {
  if (!criteriaList?.length) return undefined;

  const strong = criteriaList.find((criteria) => criteria.weight === 'strong');
  if (strong) return strong;

  const medium = criteriaList.find((criteria) => criteria.weight === 'medium');
  if (medium) return medium;

  const low = criteriaList.find((criteria) => criteria.weight === 'low');
  if (low) return low;

  return criteriaList[0];
};

export const getOptionalCriteria = (
  criteriaList: CampaignCriteria[],
  strongCriteria?: CampaignCriteria
) => {
  if (!criteriaList?.length) return [];
  if (!strongCriteria) return criteriaList;

  return criteriaList.filter((criteria) => criteria.id !== strongCriteria.id);
};

export const getCriteriaOptions = (criteria?: CampaignCriteria) => {
  if (!criteria?.options?.length) return [];

  return criteria.options.map((option) => ({
    label: option.value,
    value: option.key,
  }));
};

export const initializeCriteriaAnswers = (
  criteriaList: CampaignCriteria[]
): CriteriaAnswers => {
  const answers: CriteriaAnswers = {};

  for (const criteria of criteriaList) {
    answers[String(criteria.id)] = criteria.type === 'multiSelect' ? [] : '';
  }

  return answers;
};

export const hasSelectedCriteria = (answers: CriteriaAnswers): boolean => {
  return Object.values(answers).some((value) =>
    Array.isArray(value) ? value.length > 0 : Boolean(value)
  );
};

export const buildCriteriaPayload = (answers: CriteriaAnswers) => {
  return Object.entries(answers)
    .map(([criteriaId, value]) => {
      const isArray = Array.isArray(value);
      const hasValue = isArray ? value.length > 0 : Boolean(value);

      if (!hasValue) return null;

      return {
        criteriaId: Number(criteriaId),
        [isArray ? 'answerKeys' : 'answerKey']: value,
      };
    })
    .filter((item) => item !== null) as {
    criteriaId: number;
    answerKey?: string;
    answerKeys?: string[];
  }[];
};
